import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import { reportError } from '../utils/errorReporting';

// Simple HOC that works like React.memo()
export function withErrorBoundary<P extends object>(
    Component: React.ComponentType<P>,
    options?: {
        fallback?: React.ReactNode;
        componentName?: string;
        onError?: (error: Error, errorInfo: any) => void;
    }
) {
    const WrappedComponent = React.memo((props: P) => {
        return (
            <ErrorBoundary
                fallback={options?.fallback}
                onError={(error, errorInfo) => {
                    // Log error to console in development
                    if (process.env.NODE_ENV === 'development') {
                        console.error(`${options?.componentName || Component.name} Error:`, error, errorInfo);
                    }
                    // Report error to centralized error reporting system
                    reportError(error, errorInfo);
                    // Call custom error handler if provided
                    options?.onError?.(error, errorInfo);
                }}
            >
                <Component {...props} />
            </ErrorBoundary>
        );
    });

    // Set display name for debugging
    WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

    return WrappedComponent;
}

// Default export for easy usage
export default withErrorBoundary;